import {Component, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, ViewChild,} from '@angular/core';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {ToastrService} from 'ngx-toastr';
import {concatAll, concatMap, filter, forkJoin, from, interval, map, Observable, of, startWith, Subscription, switchMap, throwError,} from 'rxjs';
import {CourseContentTypeModel} from 'src/app/models/course/course-content-type.model';
import {CourseTemplateFullModel} from 'src/app/models/course/course-template-full.model';
import {CourseTemplateService} from 'src/app/services/course-template.service';
import {FileUtilService} from 'src/app/services/file-util.service';
import {LoadingService} from 'src/app/services/loading.service';
import {v4 as uuidv4} from 'uuid';

// @ts-ignore
import JSZip from 'jszip';
import {ContentFilePreviewModel} from '../../../models/content-file-preview.model';
import {Configuration} from 'src/assets/Configuration';
import {AuthorizationService} from 'src/app/services/authorization.service';
import {RawFileModel} from 'src/app/models/raw-file-model';
import {FileProgressService} from 'src/app/services/file-progress.service';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse } from "@angular/common/http";
import {defaultIfEmpty, take, tap} from "rxjs/operators";
import {FileModel} from "../../../models/file.model";
import {FileUploadService} from "../../../services/file-upload.service";
import { TranslateService } from '@ngx-translate/core';
import { UploadSessionCreationModel } from 'src/app/models/upload-session-creation.model';

@Component({
    selector: 'app-video-uploader',
    templateUrl: './video-uploader.component.html',
    styleUrl: './video-uploader.component.scss',
    standalone: false
})
export class VideoUploaderComponent implements OnInit {
    @Input() readOnly = false;
    @Input() isExternal = false;
    @Input() isRemoveOptionHidden = false;
    @Input() courseId!: string;
    @Input() contentFile!: File;
    @Output() contentFileChange = new EventEmitter<any>();
    @Output() isUploading: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Input() hasCustomPreviewImage = false;
    @Output() hasCustomPreviewImageChange = new EventEmitter<boolean>();

    
    contentFileFull?: ContentFileFull;

    private videoPlayer!: ElementRef<HTMLVideoElement>;

    @ViewChild('videoPlayer') set content(content: ElementRef<HTMLVideoElement>) {
        if (content) {
            this.videoPlayer = content;
        }
    }

    private subs = new Subscription();
    uploadUrl = Configuration.getApiBaseUrl() + '/fileuploader';
    accessToken: string = '';
    // videoPlayer!: ElementRef<HTMLVideoElement>;
    thumbnailUrl$: Observable<SafeUrl | string | null> = of(null);
    contentFilePreview: ContentFilePreviewModel | null = null;
    CourseContentTypeModel = CourseContentTypeModel;

    fileUploadStatus: string | null = null;
    uploadInProgress: boolean = false;

    statusSubscription: Subscription | null = null;
    shouldCall: boolean = false;

    constructor(
        private courseTemplateService: CourseTemplateService,
        private loadingService: LoadingService,
        private toastrService: ToastrService,
        private sanitizer: DomSanitizer,
        private fileUtilService: FileUtilService,
        private authorizationService: AuthorizationService,
        private fileProgressService: FileProgressService,
        private ngZone: NgZone,
        private httpClient: HttpClient,
        private fileUploadService: FileUploadService,
        private translateService: TranslateService
    ) {
        const sub = this.authorizationService.accessToken$
            .pipe(
                filter((accessToken: string | null) => accessToken != null),
                map((accessToken: string | null) => accessToken as string)
            )
            .subscribe((accessToken) => {
                this.accessToken = accessToken;
            });
        this.subs.add(sub);
    }

    ngOnInit(): void {
        if (this.courseId != "new") {
            this.uploadUrl = Configuration.getApiBaseUrl() + '/fileuploader?courseId=' + this.courseId
        }
    }

    startSubscription(fileStatusIdentifier: string) {
        this.stopSubscription();
        this.statusSubscription = interval(1500)
            .pipe(
                startWith(1),
                filter(() => {
                    return this.shouldCall;
                }),
                switchMap(() => {
                    return this.fileProgressService.getStatus(fileStatusIdentifier);
                })
            )
            .subscribe(
                (response) => {
                    this.ngZone.run(() => {
                        this.fileUploadStatus = response ? response : "Reading data...";
                    });
                },
                (error) => {
                    console.error('API error:', error);
                }
            );
    }

    stopSubscription() {
        if (this.statusSubscription) {
            this.statusSubscription.unsubscribe();
            this.statusSubscription = null;
            this.fileUploadStatus = null;
            this.uploadInProgress = false;
        }
    }

    ngOnDestroy(): void {
        this.stopSubscription();
    }

    onBeforeSend(e: any, accessToken: string): any {

        e.request.setRequestHeader('Authorization', 'Bearer ' + accessToken);
    }

    onUploaded(e: any) {

        if (this.statusSubscription) {
            this.shouldCall = false;
            this.fileUploadStatus = "";
            this.stopSubscription();
        }

        this.onSelectedFileChange([e.file], JSON.parse(e.request.response));
    }

    public setPreview(courseTemplateFull: CourseTemplateFullModel) {
        if (courseTemplateFull.contentFile) {
            const contentFilePreview: ContentFilePreviewModel = {
                contentType: courseTemplateFull.courseTemplate.courseContentType,
                name: courseTemplateFull.contentFile.fileName,
                url: courseTemplateFull.contentFile.url,
            };
            this.contentFilePreview = contentFilePreview;
            this.contentFileFull = {
                fileId: courseTemplateFull.contentFile.fileId,
                file: new File([], courseTemplateFull.contentFile.fileName),
            }
        }

        this.thumbnailUrl$ = of(
            this.hasCustomPreviewImage 
                ? (courseTemplateFull.courseTemplate.customImageFile?.fileUrl ?? null)
                : (courseTemplateFull.courseTemplate.imageFile?.fileUrl ?? null)
        );
    }

    onPreviewImageUpload(event: any): void {
        const previewImageFile: File = event.value[0];
        if (previewImageFile) {
            const previewImageFile$ = of(previewImageFile);
            this.thumbnailUrl$ = this.getPreviewImageSafeUrl(previewImageFile$);
            this.loadingService
                .load(from(this.fileUtilService.convertFileToRawFileModel(previewImageFile)))
                .subscribe(result => {
                    this.hasCustomPreviewImage = true;
                    this.hasCustomPreviewImageChange.emit(this.hasCustomPreviewImage);
                    
                    this.contentFileFull!.preview = result;
                    this.contentFileChange.emit(this.contentFileFull);
                });
            
        }
    }

    onContentDeleteClick() {
        this.contentFilePreview = null;
        this.fileUploadStatus = null;
        this.contentFileChange.emit(null);

        this.hasCustomPreviewImage = false;
        this.hasCustomPreviewImageChange.emit(this.hasCustomPreviewImage);
    }

    onPreviewImageDeleteClick() {
        this.hasCustomPreviewImage = false;
        this.hasCustomPreviewImageChange.emit(this.hasCustomPreviewImage);

        const file$ = this.getPreviewImage();
        this.thumbnailUrl$ = this.getPreviewImageSafeUrl(file$);

        this.contentFileFull!.preview = null;
        this.contentFileChange.emit(this.contentFileFull);
    }

    handleUploadStarted(e: any) {
        if (
            e.file.type === 'application/x-zip-compressed' ||
            e.file.type === 'application/zip'
        ) {
            // pass the variable to parent while uploading
            this.isUploading.emit(true);
            this.shouldCall = true;
        }
    }

    async onSelectedFileChange(
        files: File[],
        response: { fileUrl: string; fileId: string; fileName: string }
    ) {
        if (files.length > 0) {
            const obs = this.courseTemplateService.getCourseContentTypeByFileName(
                files[0].name
            );
            this.loadingService.load(obs).subscribe((contentType) => {
                if (contentType == CourseContentTypeModel.Unknown) {
                    this.toastrService.error(
                        this.translateService.instant(
                            'VALIDATIONS.FILE-NOT-SUPPORTED'
                        )
                    );
                    return;
                }

                this.contentFilePreview = {
                    contentType: contentType,
                    name: response.fileName,
                    url: response.fileUrl,
                };
                this.contentFile = files[0];
                const file$ = this.getPreviewImage();
                this.thumbnailUrl$ = this.getPreviewImageSafeUrl(file$);

                const emitContentFile = (preview?: RawFileModel | null) => {
                    const contentFile: ContentFileFull = {
                        fileId: response.fileId,
                        file: files[0],
                    };
                    if (preview !== undefined) {
                        contentFile.preview = preview;
                    }
                    this.contentFileFull = contentFile;
                    this.contentFileChange.emit(this.contentFileFull);
                };

                this.loadingService
                    .load(this.getContentFilePreviewImage$().pipe(
                        take(1),
                        defaultIfEmpty(undefined)
                    ))
                    .subscribe({
                        next: (result) => {
                            emitContentFile(result);
                        },
                        error: () => {
                            this.toastrService.error(
                                this.translateService.instant('VALIDATIONS.FILE-FORMAT-INVALID')
                            );
                            this.contentFileChange.emit(undefined);

                            this.hasCustomPreviewImage = false;
                            this.hasCustomPreviewImageChange.emit(this.hasCustomPreviewImage);

                            this.contentFilePreview = null;
                            this.fileUploadStatus = null;
                        },
                    });
            });
        } else {
            this.contentFilePreview = null;
        }
        //pass the variable to parent after upload
        this.isUploading.emit(false);

    }

    private getPreviewImageSafeUrl(file$: Observable<File|null>): Observable<SafeUrl | null> {
        return file$.pipe(
            concatMap((file) => {
                if (file) {
                    const unsafeUrl = URL.createObjectURL(file);
                    const safeUrl =
                        this.sanitizer.bypassSecurityTrustResourceUrl(unsafeUrl);
                    return of(safeUrl);
                } else {
                    return of(null);
                }
            })
        );
    }

    public getPreviewImage(): Observable<File | null> {

        if (!this.contentFilePreview) {
            return of(null);
        }

        if (
            this.contentFilePreview.contentType == CourseContentTypeModel.AzureVideo
        ) {
            const video = document.createElement('video');
            video.crossOrigin = 'Anonymous';
            video.src = this.contentFilePreview.url as string;

            return new Observable<File | null>((observer) => {
                video.addEventListener('loadeddata', () => {
                    video.currentTime = 1;
                });

                video.addEventListener('seeked', () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    const context = canvas.getContext('2d');
                    context!.drawImage(video, 0, 0, canvas.width, canvas.height);

                    const file = this.fileUtilService.convertDataUrlToFile(
                        canvas.toDataURL('image/jpeg'),
                        'preview.jpg'
                    );
                    observer.next(file);
                    observer.complete();
                });
            });
        }
        if (
            this.contentFilePreview.contentType == CourseContentTypeModel.AzureScorm
        ) {
            if (!this.contentFile) {
                return of(null);
            }
            const zip = new JSZip();
            const obs = from(zip.loadAsync(this.contentFile)).pipe(
                concatMap((filesZip) => {
                    const manifestFile = filesZip.file('imsmanifest.xml');

                    if (!manifestFile) {
                        return throwError(() => new Error('imsmanifest.xml not found in the zip file'));
                    }

                    return from(manifestFile.async('string') as Promise<string>);
                }),
                concatMap((imsManifest: string) =>
                    this.courseTemplateService.getScormThumbnailByImsManifestXml(
                        imsManifest
                    )
                ),
                concatMap((thumbnailPath) => {
                     if (!thumbnailPath) {
                        return of(null);
                    }

                    const cleanPath = this.removeSpacesFromThumnailFileName(thumbnailPath);
                    const imageFile = zip.file(cleanPath || '');

                    if (!imageFile) {
                        return of(null);
                    }

                    return forkJoin({
                        path: of(thumbnailPath),
                        blob: from(imageFile?.async('blob') || '') ?? Promise.resolve(null),
                    });
                   
                }),
                concatMap((file: { path: string | null; blob: any }|null) => {
                    if (!file || !file.path || !file.blob) {
                        return of(null);
                    }

                    const extension: string = file.path!.split('.').pop()!;
                    return of(new File([file.blob], 'preview.' + extension));
                })
            );

            return this.loadingService.load(obs);
        }
        return of(null);
    }

    getContentFilePreviewImage$(): Observable<RawFileModel | null> {
        let previewImage$: Observable<RawFileModel | null> = of(null);

        previewImage$ = this.getPreviewImage().pipe(
            filter(previewImageFile => !!previewImageFile),
            switchMap((previewImageFile) =>
                this.fileUtilService.convertFileToRawFileModel(previewImageFile as File)
            )
        );

        return previewImage$;
    }

    removeSpacesFromThumnailFileName(path: any): string {
        return path.replace(/%20/g, ' ');
    }

    async onFileChange(ev: Event) {
        const input = (ev.target as HTMLInputElement);
        const file = input.files && input.files.length > 0 ? input.files[0] : null;

        if (!file) {
            return;
        }

        if (!file.name.toLowerCase().endsWith(".zip")) {
            // Not a Zip. Regular file upload
            this.uploadInProgress = true;
            this.fileUploadStatus = "0%";

            let videoWidth: number | undefined;
            let videoHeight: number | undefined;

            if (file.type.startsWith("video/")) {
                const resolution = await this.getVideoResolution(file);

                videoWidth = resolution.width;
                videoHeight = resolution.height;
            }

            const uploadModel: UploadSessionCreationModel = {
                fileName: file.name,
                fileSizeBytes: file.size,
                contentType: file.type,
                videoWidth: videoWidth ?? null,
                videoHeight: videoHeight ?? null
            };

            this.fileUploadService
                .createUploadSession(uploadModel)
                .pipe(
                    switchMap(session =>
                        this.fileUploadService
                            .uploadDirectlyToBlob(
                                session.sasUrl,
                                file,
                                percent => {
                                    this.fileUploadStatus =
                                        percent.toFixed(1) + "%";
                                    this.uploadInProgress = percent < 100;
                                }
                            )
                            .pipe(
                                map(() => session)
                            )
                    ),
                    switchMap(session =>
                        this.fileUploadService.completeUpload(session.uploadId)
                    )
                )
                .subscribe({
                    next: uploadedFile => {
                        this.uploadInProgress = false;
                        this.fileUploadStatus = "Upload complete";

                        this.onSelectedFileChange([file], uploadedFile);
                    },
                    error: err => {
                        this.uploadInProgress = false;
                        this.toastrService.error(err.error);
                        this.fileUploadStatus = "";
                    }
                });
        } else {
            // File is a (scorm) zip. Extract all files in the client, create a folder on the server and upload each file seperatly
            let uploadedCount = 0;
            let totalFilesCount = 0;

            const zip = new JSZip();
            this.fileUploadStatus = 'Unzipping file...'
            this.fileUploadService.createFolder({name: file.name, size: file.size}).pipe(
                switchMap(folder => forkJoin([of(folder), from(zip.loadAsync(file))])),
                map(([folder, jsZip]) => {
                    const entries: { zipObject: JSZip.JSZipObject, folder: FileModel }[] = [];
                    jsZip.forEach((path: string, jsZipObject: JSZip.JSZipObject) => {
                        entries.push({zipObject: jsZipObject, folder: folder});
                    })
                    return entries;
                }),
                tap(entries => {
                    this.fileUploadStatus = 'Unzipped file...';
                    totalFilesCount = entries.length;
                }),
                concatAll(),
                concatMap(toUpload => {
                    return forkJoin([of(toUpload.folder), of(toUpload.zipObject), from(toUpload.zipObject.async("blob"))]);
                }),
                concatMap(([folder, zipObject, blob]) => {
                    uploadedCount += 1;
                    this.fileUploadStatus = 'Uploaded ' + uploadedCount + ' / ' + totalFilesCount + ' files';
                    return this.fileUploadService.uploadToFolder(folder.fileId, zipObject.name, blob);
                }),
                filter((event: HttpEvent<FileModel>) => event.type == HttpEventType.Response),
                map(ev => ev as HttpResponse<FileModel>)
            ).subscribe({
                next: response => {
                    if (uploadedCount == totalFilesCount) {
                        this.fileUploadStatus = 'Upload complete';
                        this.onSelectedFileChange([file], response.body!);
                    }
                },
                error: (err: HttpErrorResponse) => {
                    this.toastrService.error(err.status + " " + err.message);
                },
            });
        }
    }

    private getVideoResolution(
        file: File
    ): Promise<{ width: number; height: number }> {
        return new Promise((resolve, reject) => {
            const video = document.createElement("video");
            const url = URL.createObjectURL(file);

            video.onloadedmetadata = () => {
                resolve({
                    width: video.videoWidth,
                    height: video.videoHeight
                });

                URL.revokeObjectURL(url);
            };

            video.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error("Could not determine video resolution."));
            };

            video.src = url;
        });
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        const files = event.dataTransfer?.files;

        if (files && files.length > 0) {
          this.handleDroppedFile(files[0]);
        }
    }

    handleDroppedFile(file: File): void {
        const fileList = new DataTransfer();
        fileList.items.add(file);

        const event = {
            target: {
                files: fileList.files
            }
        } as any;

        this.onFileChange(event);
    }
}

interface ContentFileFull {
    fileId: string;
    file: File;
    preview?: RawFileModel | null;
}